import { Decimal } from 'decimal.js';
import type { Row } from './row';

export const TRANSFER_FLOOR = -5;
export const TRANSFER_CEILING = 1;

/** Net result of the displayed stakes, before formatting to two places. */
export function transferRoi(row: Pick<Row, 'profit' | 'staked' | 'netRoiExact'>): Decimal {
  if (row.netRoiExact !== undefined) return new Decimal(row.netRoiExact);
  return row.staked > 0 ? new Decimal(row.profit).div(row.staked).times(100) : new Decimal(NaN);
}

export function transferBound(value: number | null | undefined, fallback: number): number {
  return value !== null && value !== undefined && Number.isFinite(value)
    ? Math.max(TRANSFER_FLOOR, Math.min(TRANSFER_CEILING, value)) : fallback;
}

export function transferFloor(value: number | null | undefined): number {
  return transferBound(value, TRANSFER_FLOOR);
}

/** Structural checks: two legs, one Polymarket, NET inside −5%…+1%. */
export function transferEligible(row: Row, floor = TRANSFER_FLOOR, favouriteOnly = false): boolean {
  const legs = row.fork.legs ?? [];
  if (legs.length !== 2 || legs.filter((leg) => leg.s === 'polymarket').length !== 1) return false;
  const book = legs.find((leg) => leg.s !== 'polymarket');
  if (!book || !book.s || book.s === 'hidden') return false;
  if (favouriteOnly) {
    const poly = row.legs?.find((leg) => leg.source === 'polymarket');
    if (!poly) return false;
    try {
      const odds = new Decimal(poly.odds);
      if (!odds.isFinite() || !odds.gt(1) || !odds.lt(2)) return false;
    } catch { return false; }
  }
  const roi = transferRoi(row);
  const low = transferFloor(floor);
  return row.staked > 0 && roi.isFinite() && roi.gte(low) && roi.lte(TRANSFER_CEILING);
}
